// cn 은 디자인 시스템(@workspace/ui)이 소유한다. 구현이 동일하므로 재수출만 한다.
export { cn } from "@workspace/ui/lib/utils";

/**
 * id 로 요소를 찾아 스크롤 이동. prefers-reduced-motion 사용자는 즉시 이동.
 * 셸 좌측 네비(문제 목록·복원 문제 네비) 등 앵커 이동 공용 헬퍼(JCG-2102).
 */
export function scrollToAnchor(
  id: string,
  block: ScrollLogicalPosition = "start",
): void {
  if (typeof window === "undefined") return;
  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  document.getElementById(id)?.scrollIntoView({
    behavior: reduceMotion ? "auto" : "smooth",
    block,
  });
}

/**
 * 페이지 최상단(top:0)으로 스크롤. prefers-reduced-motion 사용자는 즉시 이동.
 * "맨 위로" FAB(ScrollToTopButton, JCG-2973) 등 top:0 이동 공용 헬퍼.
 */
export function scrollToTop(): void {
  if (typeof window === "undefined") return;
  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
}

/**
 * 페이지 이동 중 서버 액션 응답 처리 실패로 발생하는 에러인지 확인.
 * Next.js는 서버 액션이 진행 중일 때 클라이언트 네비게이션이 발생하면
 * 응답 형식 불일치로 이 에러를 던진다. 사용자가 이미 다른 페이지로
 * 이동했으므로 에러 표시나 Sentry 보고가 불필요하다.
 */
export function isServerActionNavigationError(error: unknown): boolean {
  return error instanceof Error &&
    error.message === 'An unexpected response was received from the server.';
}

/**
 * Clerk `getToken()`을 최대 `retries + 1`회까지 재시도.
 *
 * 단발 `getToken()` 호출은 네트워크 지연/토큰 갱신 경쟁 조건에서
 * 일시적으로 null을 반환할 수 있는데, 이 값을 그대로 "세션 만료"로
 * 간주하고 `/sign-in`으로 강제 리다이렉트하면 정상 로그인 사용자가
 * 튕겨나가는 false-positive가 발생한다.
 *
 * 실제 세션 만료는 재시도해도 동일하게 null이므로, 1~2회 짧은 대기 후
 * 다시 호출해 확실한 만료일 때만 null을 반환하도록 한다.
 *
 * @param getToken Clerk `useAuth().getToken` 함수
 * @param retries 재시도 횟수 (기본 1 → 총 2회 호출)
 * @param delayMs 재시도 사이 대기(ms, 기본 400)
 */
export async function getTokenWithRetry(
  getToken: () => Promise<string | null>,
  retries: number = 1,
  delayMs: number = 400
): Promise<string | null> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    const token = await getToken().catch(() => null);
    if (token) return token;
    if (attempt < retries) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  return null;
}
